/**
 * Video Templates API
 *
 * Manage video templates (public and admin views), creation with uploads,
 * signed URLs, and usage tracking. Includes public endpoint via `SERVER_URL`.
 */
import { get, post, put, del, SERVER_URL } from '../core';

/**
 * Gets all video templates for public access (no admin required)
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{videoTemplates: Array, page: number, limit: number, hasMore: boolean}>} Video templates data
 */
export function getAllVideoTemplates(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/videoTemplates?${params.toString()}`);
}

/**
 * Fetches public video templates without requiring authentication
 * Returns obfuscated data (no canva links)
 * @returns {Promise<Array>} - List of public video templates with minimal information
 */
export function getPublicVideoTemplates() {
  // Using the external API endpoint instead of internal API
  return fetch(`${SERVER_URL}/publicVideoTemplates`)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    });
}

/**
 * Save a video template to a namespace
 * @param {string} videoTemplateId - The video template ID
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Object>} Response data
 */
export const saveVideoTemplate = async (videoTemplateId, namespaceId) => {
  try {
    if (!namespaceId) {
      throw new Error('Namespace ID is required to save video templates');
    }
    
    const response = await post(`/videoTemplates/save/${videoTemplateId}?namespaceId=${encodeURIComponent(namespaceId)}`, {});
    return response.data;
  } catch (error) {
    console.error('Error saving video template:', error);
    throw error;
  }
};

/**
 * Unsave a video template from a namespace
 * @param {string} videoTemplateId - The video template ID
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Object>} Response data
 */
export const unsaveVideoTemplate = async (videoTemplateId, namespaceId) => {
  try {
    if (!namespaceId) {
      throw new Error('Namespace ID is required to unsave video templates');
    }
    
    const response = await del(`/videoTemplates/save/${videoTemplateId}?namespaceId=${encodeURIComponent(namespaceId)}`);
    return response.data;
  } catch (error) {
    console.error('Error unsaving video template:', error);
    throw error;
  }
};

/**
 * Get saved video template IDs for a specific namespace
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Array>} Array of saved video template IDs
 */
export const getSavedVideoTemplateIds = async (namespaceId) => {
  try {
    if (!namespaceId) {
      console.warn('No namespace ID provided, cannot fetch saved video templates');
      return [];
    }
    
    // Use the namespaces endpoint to get namespace details including savedVideoTemplates
    const namespaceData = await get(`/namespaces/${namespaceId}`);
    
    // Extract savedVideoTemplates from the namespace data
    if (namespaceData && Array.isArray(namespaceData.savedVideoTemplates)) {
      return namespaceData.savedVideoTemplates;
    }
    
    // In case the savedVideoTemplates array doesn't exist
    return [];
  } catch (error) {
    // If endpoint doesn't exist (404), return empty array
    if (error.message?.includes('404') || error.status === 404) {
      console.log('Video templates saved endpoint not yet implemented, returning empty array');
      return [];
    }
    console.error('Error getting saved video template IDs:', error);
    return []; // Return empty array on error to avoid breaking the UI
  }
};

/**
 * Generates video template data using AI based on a prompt
 * @param {string} prompt - The video template description prompt
 * @returns {Promise<{status: string, videoTemplateData: Object, message?: string}>} AI generated data
 */
export function generateVideoTemplateWithAI(prompt) {
  return post('/videoTemplates/generate-video-template', { prompt });
}

/**
 * Gets a video template upload URL from the server
 * @returns {Promise<{uploadUrl: string, permanentUploadPath: string}>} Upload URL and permanent path
 */
export function getVideoTemplateUploadUrl() {
  return get('/videoTemplates/video-upload-url');
}

/**
 * Creates a new video template
 * @param {FormData} formData - The video template form data
 * @returns {Promise<{message: string, videoTemplateId: string}>} Success response
 */
export function createVideoTemplate(formData) {
  return post('/videoTemplates/create', formData);
}

/**
 * Gets all video templates for admin management
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{videoTemplates: Array, totalCount: number, currentPage: number, totalPages: number}>} Video templates data
 */
export function getAllVideoTemplatesForAdmin(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/videoTemplates/all?${params.toString()}`);
}

/**
 * Gets a single video template by ID
 * @param {string} videoTemplateId - The video template ID
 * @returns {Promise<{videoTemplate: Object}>} Video template data
 */
export function getVideoTemplate(videoTemplateId) {
  return get(`/videoTemplates/${videoTemplateId}`);
}

/**
 * Updates an existing video template
 * @param {string} videoTemplateId - The video template ID
 * @param {FormData} formData - The form data containing updated fields
 * @returns {Promise<{message: string}>} Success response
 */
export function updateVideoTemplate(videoTemplateId, formData) {
  return put(`/videoTemplates/${videoTemplateId}`, formData);
}

/**
 * Gets a signed URL for viewing a video template
 * @param {string} videoTemplateId - The video template ID
 * @returns {Promise<{signedUrl: string, title: string, expiresAt: string}>} Signed URL data
 */
export function getVideoTemplateSignedUrl(videoTemplateId) {
  return get(`/videoTemplates/${videoTemplateId}/signed-url`);
}

/**
 * Deletes a video template
 * @param {string} videoTemplateId - The video template ID
 * @returns {Promise<{message: string}>} Success response
 */
export function deleteVideoTemplate(videoTemplateId) {
  return del(`/videoTemplates/${videoTemplateId}`);
}

/**
 * Record video template usage when a user clicks the Canva link
 * @param {string} videoTemplateId - The ID of the video template being used
 * @returns {Promise<Object>} - The success response
 */
export async function useVideoTemplate(videoTemplateId) {
  try {
    return await post(`/videoTemplates/use/${videoTemplateId}`, {});
  } catch (error) {
    console.error('Error recording video template usage:', error);
    // Don't throw the error - this is a non-critical operation
    return null;
  }
} 